import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, BookOpen, Atom, Calculator, FlaskConical, Globe,
  Languages, Music, Palette, Cpu, Leaf, Brain, Heart, Trash2, Flame, Trophy
} from "lucide-react";
import { listSubjects, deleteSubject } from "@/lib/api";
import CreateSubjectDialog from "@/components/CreateSubjectDialog";
import { SUBJECT_COLORS } from "@/lib/subjectMeta";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const ICONS = { BookOpen, Atom, Calculator, FlaskConical, Globe, Languages, Music, Palette, Cpu, Leaf, Brain, Heart };

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setSubjects(await listSubjects());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this subject and all its content?")) return;
    try {
      await deleteSubject(id);
      setSubjects((curr) => curr.filter((s) => s.id !== id));
      toast.success("Subject deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="dashboard" className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps text-zinc-600 dark:text-zinc-400 mb-2">
            Hi {user?.name?.split(" ")[0] || "there"} 👋
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            Your study<br />
            <span className="bg-[#9DFFB0] dark:bg-[#68D391] px-3 nb-border rounded-2xl inline-block">
              command center
            </span>
          </h1>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="nb-border rounded-xl px-4 py-3 bg-[#FFB38E] dark:bg-[#F6AD55] flex items-center gap-2 text-black">
            <Flame className="w-5 h-5" strokeWidth={2.5} />
            <span className="font-bold">{user?.streak || 0} day streak</span>
          </div>
          <div className="nb-border rounded-xl px-4 py-3 bg-[#FFE066] dark:bg-[#F6E05E] flex items-center gap-2 text-black">
            <Trophy className="w-5 h-5" strokeWidth={2.5} />
            <span className="font-bold">{user?.xp || 0} XP</span>
          </div>
          <button
            data-testid="create-subject-button"
            onClick={() => setOpenCreate(true)}
            className="nb-button px-5 py-3 bg-[#D4C4FB] dark:bg-[#B794F4] flex items-center gap-2"
          >
            <Plus className="w-5 h-5" strokeWidth={3} />
            New Subject
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-zinc-500 font-bold">Loading…</div>
      ) : subjects.length === 0 ? (
        <div className="nb-border rounded-2xl p-10 bg-white dark:bg-zinc-900 text-center nb-shadow">
          <div className="font-display text-3xl font-extrabold mb-2">No subjects yet</div>
          <p className="text-zinc-600 dark:text-zinc-400 mb-6">
            Create your first subject to start uploading notes and generating revision content.
          </p>
          <button
            data-testid="create-first-subject"
            onClick={() => setOpenCreate(true)}
            className="nb-button px-6 py-3 bg-[#FFE066] dark:bg-[#F6E05E]"
          >
            + Create your first subject
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {subjects.map((s, idx) => {
            const Icon = ICONS[s.icon] || BookOpen;
            const color = SUBJECT_COLORS[s.color] || SUBJECT_COLORS.primary;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => navigate(`/subjects/${s.id}`)}
                data-testid={`subject-card-${s.id}`}
                className="nb-card cursor-pointer p-6 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-xl nb-border ${color.chip} flex items-center justify-center`}>
                    <Icon className="w-7 h-7 text-black" strokeWidth={2.5} />
                  </div>
                  <button
                    onClick={(e) => handleDelete(e, s.id)}
                    data-testid={`delete-subject-${s.id}`}
                    className="opacity-0 group-hover:opacity-100 nb-border rounded-lg p-2 bg-white dark:bg-zinc-900 hover:bg-red-100 dark:hover:bg-red-900 transition-opacity"
                    aria-label="delete subject"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="font-display text-2xl font-extrabold mb-3 truncate">{s.name}</h3>
                <div className="grid grid-cols-2 gap-1 text-xs font-bold text-zinc-700 dark:text-zinc-300">
                  <div>📄 {s.counts?.notes || 0} notes</div>
                  <div>📘 {s.counts?.books || 0} books</div>
                  <div>🧠 {s.counts?.flashcards || 0} cards</div>
                  <div>🎙 {s.counts?.podcasts || 0} pods</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <CreateSubjectDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={(s) => { setSubjects((curr) => [s, ...curr]); refresh(); }}
      />
    </div>
  );
}
